import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {Observable, of} from 'rxjs';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {shareReplay} from 'rxjs/operators';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {GATEWAY_TYPE} from '@constants/gateway/gateway-type';
import {GatewaySignals} from '@models/adminstration/gateway/gateway-configuration/gateway-signals';
import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';
import {GatewayArray} from '@models/adminstration/gateway/gateway-configuration/gateway-array';
import {Connector} from '@models/adminstration/gateway/connector';
import {Log} from '@models/adminstration/gateway/log';
import {MetaData} from '@models/adminstration/meta-data';
import {GatewayIdList} from '@models/gateway/gateway-id-list';
import {
  GatewayConfigurationRequest
} from '@models/adminstration/gateway/gateway-configuration/gateway-configuration-request';
import {
  GatewayCommunicationRequest
} from '@models/adminstration/gateway/gateway-communication/gateway-communication-request';
import {UPDATE_STATE} from '@models/gateway/update/update-state.constant';
import {GATEWAY_STATUS} from '@constants/gateway/gateway-status';


@Injectable({
  providedIn: 'root'
})
export class GatewayService {

  private GATEWAY_SAVE_URL_SUFFIX = '/api/web/admin/cloudplug/:id/create?sourceId=:sourceId';
  private GATEWAY_DELETE_URL_SUFFIX = '/api/web/admin/cloudplug/:id';
  private GATEWAY_GET_REGISTRY_ID_URL_SUFFIX = '/api/web/admin/cloudplug/cloudIOT/registryIds';
  private GATEWAY_RESET_KEY_URL_SUFFIX = '/api/web/admin/cloudplug/:id/resetSecretKey';
  private GATEWAY_UPDATE_URL_SUFFIX = '/api/web/admin/cloudplug/updatePassedCloudplugs';
  private GATEWAY_DETAILS_URL_SUFFIX = '/api/web/admin/cloudplug/:id';
  private GATEWAY_NEXT_UPDATE_URL_SUFFIX = '/api/web/admin/cloudplug/:id/nextUpdate/:date';
  private GATEWAY_LIST_CLOUDPLUG_SIGNALS_URL_SUFFIX = '/api/web/admin/cloudplug/:id/signals';
  private GATEWAY_DELETE_ALIAS_URL_SUFFIX = '/api/web/admin/cloudplug/:id/aliasDelete?alias=:aliasId&type=:gatewayType';
  private GATEWAY_TOGGLE_CONNECTOR_STATUS_URL_SUFFIX = '/api/web/admin/cloudplug/:id/services';
  private GATEWAY_GET_CONNECTOR_STATUS_URL_SUFFIX = '/api/web/admin/cloudplug/:id/services/:connectorId';
  private GATEWAY_GET_IBA_CONFIG_URL_SUFFIX = '/api/web/admin/cloudplug/:id/ipaConfig';
  private GATEWAY_SAVE_IBA_CONFIG_URL_SUFFIX = '/api/web/admin/cloudplug/:id/ipaConfig';
  private GATEWAY_GET_LOGS_URL_SUFFIX = '/api/web/admin/cloudplug/:id/logs';
  private GATEWAY_SAVE_SIGNAL_CONFIGURATION_URL_SUFFIX = '/api/web/admin/cloudplug/:id/signals';
  private GATEWAY_PUSH_TO_IOT_CORE_URL_SUFFIX = '/api/web/admin/cloudplug/:id/signals/iotcore';

  private GATEWAY_AVAILABLE_FOR_USER_URL_SUFFIX = '/api/web/admin/cloudplug/:id/cloudPlugIsAvailableForUser';

  private PORTAL_SETTING_EAV_VIEW_ENABLED_URL_SUFFIX = '/api/web/admin/cloudplug/eavViewFeatureEnabled';
  private PORTAL_SETTING_STREAM_EVENT_DATA_SUFFIX = '/api/web/admin/cloudplug/eventDataStreamToPubSubFeatureEnabled';

  private GATEWAY_CONNECTIVITY_LOGS_URL_SUFFIX = '/api/web/admin/cloudplug/connectivityLogs/:tenantId/:id';

  private GATEWAY_COMMUNICATION_URL_SUFFIX = '/api/web/admin/cloudplug/:id/communication';

  private GATEWAY_METADATA_SAVE_URL_SUFFIX = '/api/web/admin/cloudplug/:id/metadata/save';
  private GATEWAY_METADATA_DELETE_URL_SUFFIX = '/api/web/admin/cloudplug/:id/metadata/delete/:metaDataKey';

  private eavViewFeatureResponseCache = new Map();
  private streamingEventDataFeatureResponseCache = new Map();

  private readonly BASE_URL: string;

  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  public listConnectivityLogs(tenantId: string, gatewayId: string, eventFilter: string[]): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_CONNECTIVITY_LOGS_URL_SUFFIX;
    url = url.replace(':id', gatewayId);
    url = url.replace(':tenantId', tenantId);
    return this.http.post(url, eventFilter, HTTPS_REQUEST_OPTIONS);
  }

  public addGateway(sourceId: number, gateway: Gateway): Observable<any> {
    const request = {
      cloudplugType: gateway.cloudplugType,
      id: gateway.id,
      iotCore: gateway.iotCore,
      name: gateway.name,
      parentId: gateway.machineId,
      tenantId: gateway.tenantId,
    };
    let url: string = this.BASE_URL + this.GATEWAY_SAVE_URL_SUFFIX;
    url = url.replace(':id', String(gateway.id))
    .replace(':sourceId', String(sourceId));
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public deleteGateway(gatewayId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_DELETE_URL_SUFFIX;
    url = url.replace(':id', String(gatewayId));
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public getIotRegistryIds(): Observable<any> {
    const url: string = this.BASE_URL + this.GATEWAY_GET_REGISTRY_ID_URL_SUFFIX;
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public resetSecretKey(gatewayId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_RESET_KEY_URL_SUFFIX;
    url = url.replace(':id', String(gatewayId));
    return this.http.post(url, gatewayId, HTTPS_REQUEST_OPTIONS);
  }

  public updateGateways(gatewayIdList: GatewayIdList): Observable<any> {
    const url: string = this.BASE_URL + this.GATEWAY_UPDATE_URL_SUFFIX;
    return this.http.post(url, gatewayIdList, HTTPS_REQUEST_OPTIONS);
  }

  public getGatewayDetails(gatewayId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_DETAILS_URL_SUFFIX;
    url = url.replace(':id', gatewayId);
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public updateGateway(gateway: Gateway, date: string): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_NEXT_UPDATE_URL_SUFFIX;
    url = url.replace(':id', gateway.id);
    url = url.replace(':date', date);
    return this.http.post(url, gateway, HTTPS_REQUEST_OPTIONS);
  }

  public saveGateway(gateway: Gateway): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_DETAILS_URL_SUFFIX;
    url = url.replace(':id', gateway.id);
    return this.http.post(url, gateway, HTTPS_REQUEST_OPTIONS);
  }

  public getGatewaySignals(gatewayId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_LIST_CLOUDPLUG_SIGNALS_URL_SUFFIX;
    url = url.replace(':id', gatewayId);
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public deleteAlias(gatewayId: string, aliasId: number, gatewayType: GATEWAY_TYPE): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_DELETE_ALIAS_URL_SUFFIX;
    url = url.replace(':id', gatewayId);
    url = url.replace(':aliasId', String(aliasId));
    url = url.replace(':gatewayType', gatewayType);
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public saveGatewayConnection(gatewayId: string, gatewaySignals: GatewaySignals[]): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_LIST_CLOUDPLUG_SIGNALS_URL_SUFFIX;
    url = url.replace(':id', gatewayId);
    return this.http.post(url, {configurationList: gatewaySignals}, HTTPS_REQUEST_OPTIONS);
  }

  public nameAlreadyUsedValidator(arrays: GatewayArray[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const equal = this.isArrayNameAlreadyUsed(control.value, arrays);
      return equal ? {equalName: {value: control.value}} : null;
    };
  }

  public saveMetaData(gatewayId: string, metaData: MetaData): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_METADATA_SAVE_URL_SUFFIX;
    url = url.replace(':id', gatewayId);
    return this.http.post<MetaData>(url, metaData, HTTPS_REQUEST_OPTIONS);
  }

  public deleteMetaData(gatewayId: string, deleteMetaData: MetaData): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_METADATA_DELETE_URL_SUFFIX;
    url = url.replace(':id', gatewayId).replace(':metaDataKey', String(deleteMetaData.key));

    return this.http.delete<MetaData>(url, HTTPS_REQUEST_OPTIONS);
  }

  public toggleConnectorStatus(gatewayId: string, connector: Connector): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_TOGGLE_CONNECTOR_STATUS_URL_SUFFIX;
    url = url.replace(':id', gatewayId);
    return this.http.post<Connector>(url, connector, HTTPS_REQUEST_OPTIONS);
  }

  public getConnectorStatus(gatewayId: string, connectorId: number): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_GET_CONNECTOR_STATUS_URL_SUFFIX;
    url = url.replace(':id', gatewayId);
    url = url.replace(':connectorId', String(connectorId));
    return this.http.get<Connector>(url, HTTPS_REQUEST_OPTIONS);
  }

  public getIbaConfig(gatewayId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_GET_IBA_CONFIG_URL_SUFFIX;
    url = url.replace(':id', gatewayId);
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public saveIbaConfig(gatewayId: string, ipaConfig: any): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_SAVE_IBA_CONFIG_URL_SUFFIX;
    url = url.replace(':id', gatewayId);
    return this.http.post(url, ipaConfig, HTTPS_REQUEST_OPTIONS);
  }

  public getLogs(gatewayId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_GET_LOGS_URL_SUFFIX;
    url = url.replace(':id', gatewayId);
    return this.http.get<Log[]>(url, HTTPS_REQUEST_OPTIONS);
  }

  public saveSignalConfiguration(gatewayId: string, gatewayConfigurationRequest: GatewayConfigurationRequest): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_SAVE_SIGNAL_CONFIGURATION_URL_SUFFIX;
    url = url.replace(':id', gatewayId);
    return this.http.post(url, gatewayConfigurationRequest, HTTPS_REQUEST_OPTIONS);
  }

  public pushToIotCore(gatewayId: string, gatewaySignals: GatewaySignals): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_PUSH_TO_IOT_CORE_URL_SUFFIX;
    url = url.replace(':id', gatewayId);
    return this.http.post(url, gatewaySignals, HTTPS_REQUEST_OPTIONS);
  }

  public isEavViewFeatureEnabled(): Observable<any> {
    const url = this.BASE_URL + this.PORTAL_SETTING_EAV_VIEW_ENABLED_URL_SUFFIX;

    const flagFromCache = this.eavViewFeatureResponseCache.get(url);
    if (flagFromCache) {
      return of(flagFromCache);
    }
    const response = this.http.get<boolean[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    response.subscribe((result) => {
      this.eavViewFeatureResponseCache.set(url, result);
    });
    return response;

  }

  public isStreamingEventDataToPubSubFeatureEnabled(): Observable<any> {
    const url = this.BASE_URL + this.PORTAL_SETTING_STREAM_EVENT_DATA_SUFFIX;

    const flagFromCache = this.streamingEventDataFeatureResponseCache.get(url);
    if (flagFromCache) {
      return of(flagFromCache);
    }
    const response = this.http.get<boolean[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    response.subscribe((result) => {
      this.streamingEventDataFeatureResponseCache.set(url, result);
    });
    return response;

  }

  public gatewayIsAvailableForUser(gatewayId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_AVAILABLE_FOR_USER_URL_SUFFIX;
    url = url.replace(':id', String(gatewayId));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public gatewayCommunication(gatewayId: string, request: GatewayCommunicationRequest): Observable<any> {
    let url: string = this.BASE_URL + this.GATEWAY_COMMUNICATION_URL_SUFFIX;
    url = url.replace(':id', gatewayId);

    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }


  public getGatewayStatusClasses(): { [key in GATEWAY_STATUS]: string } {
    return {
      [GATEWAY_STATUS.ONLINE]: 'status-success',
      [GATEWAY_STATUS.PARTIALLY_DISCONNECTED]: 'status-warning',
      [GATEWAY_STATUS.OFFLINE_OR_UNKNOWN]: 'status-danger',
      [GATEWAY_STATUS.MISSING]: 'status-danger',
    };
  }

  public getUpdateStateClasses(): { [key in UPDATE_STATE]: string } {
    return {
      [UPDATE_STATE.SUCCESS]: 'badge-success',
      [UPDATE_STATE.ERROR]: 'badge-danger',
      [UPDATE_STATE.NONE]: 'badge-danger',
      [UPDATE_STATE.AVAILABLE]: 'badge-warning',
      [UPDATE_STATE.PENDING]: 'badge-warning',
      [UPDATE_STATE.PROGRESS]: 'badge-warning',
      [UPDATE_STATE.REQUESTED]: ''
    };
  }

  private isArrayNameAlreadyUsed(arrayName: string, arrays: GatewayArray[]): boolean {
    const tmpNames: string[] = [];
    if (!arrays || arrays.length <= 0) {
      return false;
    }
    for (const arr of arrays) {
      tmpNames.push(arr.name);
    }

    return tmpNames.indexOf(arrayName) > -1;
  }
}
